import * as FileIcons from '@/components/atoms/icons/file'
import { Span } from '@/components/atoms'
import { type FileUploadVariant, useFileUpload } from './file-upload.hook'

interface FileUploadProps {
  label: string
  variant?: FileUploadVariant
  previewUrl?: string
  selectedFileName?: string
  /** Allows picking several files at once; they are emitted via `onFilesSelect` */
  multiple?: boolean
  error?: string
  onFileSelect?: (file: File) => void
  onFilesSelect?: (files: File[]) => void
  className?: string
}

export function FileUpload({
  label,
  variant = 'image',
  previewUrl,
  selectedFileName,
  multiple = false,
  error,
  onFileSelect,
  onFilesSelect,
  className = '',
}: FileUploadProps) {
  const {
    inputRef,
    isDragging,
    handleClick,
    handleChange,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    VariantIcon,
    uploadLabel,
    selectLabel,
    changeLabel,
    formats,
    accept,
    variantClassName,
  } = useFileUpload({ variant, multiple, onFileSelect, onFilesSelect })

  return (
    <div className={`flex w-full flex-col gap-3 ${className}`}>
      <p className="tracking-base text-base text-neutral-900">{label}</p>

      <div
        className={`relative flex items-center justify-center overflow-hidden rounded-2xl border border-dashed bg-(--file-upload-bg) transition-colors ${isDragging ? 'border-indigo-base bg-indigo-50' : error ? 'border-error-base' : 'border-neutral-300'} ${variantClassName}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <>
            {variant === 'video' ? (
              <video
                src={previewUrl}
                className="absolute inset-0 size-full object-contain"
                controls={false}
                muted
              />
            ) : (
              <img
                src={previewUrl}
                alt=""
                className="absolute inset-0 size-full object-contain"
              />
            )}
            <button
              type="button"
              onClick={handleClick}
              className="border-indigo-base text-indigo-base tracking-base relative z-10 flex h-8 items-center gap-1 rounded-full border bg-white/80 pr-4 pl-3.5 text-xs backdrop-blur-sm"
            >
              <FileIcons.CloudUploadIcon className="size-4" />
              {changeLabel}
            </button>
          </>
        ) : selectedFileName ? (
          <div className="flex flex-col items-center gap-2">
            <VariantIcon className="size-8 text-neutral-600" />
            <p className="tracking-base max-w-[80%] truncate text-xs leading-4 text-neutral-900">
              {selectedFileName}
            </p>
            <button
              type="button"
              onClick={handleClick}
              className="border-indigo-base text-indigo-base tracking-base flex h-8 items-center gap-1 rounded-full border pr-4 pl-3.5 text-xs"
            >
              <FileIcons.CloudUploadIcon className="size-4" />
              {changeLabel}
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <VariantIcon className="size-8 text-neutral-300" />

            <div className="flex flex-col items-center gap-0.5 text-center">
              <p className="tracking-base text-xs leading-4 font-bold text-neutral-900">
                {uploadLabel}
              </p>
              <p className="tracking-base text-xs leading-4 text-neutral-600">
                {formats}
              </p>
            </div>

            <button
              type="button"
              onClick={handleClick}
              className="border-indigo-base text-indigo-base tracking-base flex h-8 items-center gap-1 rounded-full border pr-4 pl-3.5 text-xs"
            >
              <FileIcons.CloudUploadIcon className="size-4" />
              {selectLabel}
            </button>
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleChange}
      />

      {error && <Span className="text-error-base">{error}</Span>}
    </div>
  )
}
